import React, {
  CSSProperties,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { throttle } from 'lodash';

import BackToTopButton from '../../../../components/back-to-top-button/back-to-top-button';

import {
  useCompareProducts,
  useScrollTo
} from '../../../../shared/hooks/hooks';

interface PlpBackToTopBtnProps {
  bottomDisplaceRef: RefObject<HTMLDivElement>;
  containerRef: RefObject<HTMLDivElement>;
}

function PlpBackToTopButton(props: PlpBackToTopBtnProps) {
  const { bottomDisplaceRef, containerRef } = props;
  const [btnStyleObj, setBtnStyleObj] = useState<CSSProperties>({
    bottom: 0,
    display: 'none',
    paddingBottom: '30px',
    position: 'fixed',
    right: '50%'
  });
  const btnContainerRef = useRef<HTMLDivElement>(null);
  const { productsForComparison } = useCompareProducts();
  const scrollToTop = useScrollTo(null, { offset: 120 }, containerRef.current);

  const getBottomPosition = useCallback(() => {
    let paddingBottom, position;
    let bottomDisplace = 0;

    if (bottomDisplaceRef.current && btnContainerRef.current) {
      const bottomDisplaceRect =
        bottomDisplaceRef.current.getBoundingClientRect();
      const btnContainerRect = btnContainerRef.current.getBoundingClientRect();

      if (
        btnContainerRect.bottom !== 0 &&
        btnContainerRect.bottom < window.innerHeight
      ) {
        if (bottomDisplaceRect.bottom - window.innerHeight > 0) {
          bottomDisplace = bottomDisplaceRect.bottom - window.innerHeight + 17;
        }
      } else {
        bottomDisplace = bottomDisplaceRect.height;
      }
    }

    if (
      containerRef.current &&
      window.innerHeight < containerRef.current.getBoundingClientRect().bottom
    ) {
      paddingBottom = `${bottomDisplace + 30}px`;
      position = 'fixed';
    } else {
      paddingBottom = `${bottomDisplace + 13}px`;
      position = 'absolute';
    }

    return { paddingBottom, position };
  }, [bottomDisplaceRef, containerRef]);

  const handleBtnDisplay = useCallback(() => {
    const newStyleObj = { ...btnStyleObj };
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();

      if (containerRect.top > -1000) {
        newStyleObj.display = 'none';
      } else {
        newStyleObj.display = 'initial';

        const { paddingBottom, position } = getBottomPosition();
        newStyleObj.paddingBottom = paddingBottom;
        newStyleObj.position = position;

        const halfContainer = containerRect.width / 2;
        if (position === 'fixed') {
          const marginRight = document.body.clientWidth - containerRect.right;
          newStyleObj.right = `${halfContainer + marginRight}px`;
        } else {
          newStyleObj.right = `${halfContainer}px`;
        }
      }
    }
    setBtnStyleObj({ ...newStyleObj });
  }, [btnStyleObj, containerRef, getBottomPosition]);

  useEffect(() => {
    const throttledBtnDisplay = throttle(handleBtnDisplay, 250);
    window.addEventListener('scroll', throttledBtnDisplay);
    return () => {
      window.removeEventListener('scroll', throttledBtnDisplay);
    };
  }, [handleBtnDisplay, productsForComparison]);

  useEffect(() => {
    const { paddingBottom, position } = getBottomPosition();
    setBtnStyleObj(prev => ({ ...prev, paddingBottom, position }));
  }, [getBottomPosition, productsForComparison.length]);

  return (
    <BackToTopButton
      containerRef={btnContainerRef}
      onClick={scrollToTop}
      styleObj={btnStyleObj}
    />
  );
}

export default PlpBackToTopButton;
